<template>
  <b-modal
    id="assign-users-tags"
    :title="`Assign ${tag.name} Tag to Users`"
    ok-only
    ok-title="Close"
  >
    <b-overlay :show="isLoading">
      <b-form-group>
        <b-form-input
          v-model="keyword"
          :placeholder="$t('Search Users')"
          @input="getUsers"
        />
      </b-form-group>

      <b-table
        id="project-users-table"
        :items="users"
        :fields="fields"
        responsive
        show-empty
      >
        <template #head()="data">
          <span class="text-capitalize">
            {{ data.label }}
          </span>
        </template>

        <template #cell(name)="data">
          <div class="d-flex align-items-center">
            <b-avatar
              :src="getAvatar(data.item.avatar)"
              size="30"
              badge
              :badge-variant="data.item.isOnline ? 'success' : 'secondary'"
              :text="data.item.text.charAt(0)"
              class="mr-1"
              variant="light-primary"
            />
            <span class="mb-0">{{ IS_MOBILE() ? FORMAT_NAME(data.item.text) : data.item.text }}</span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-form-checkbox
            :checked="data.item.tags.map(tag => tag.name).includes(tag.name)"
            class="colorful-switch"
            switch
            @change="value => updateTag(data.item, value)"
          >
            <span class="switch-icon-left">
              <feather-icon
                icon="CheckIcon"
                color="#ffffff"
              />
            </span>
            <span class="switch-icon-right">
              <feather-icon
                icon="XIcon"
                color="#ffffff"
              />
            </span>
          </b-form-checkbox>
        </template>
      </b-table>

      <div class="float-right">
        <!-- Pagination -->
        <b-pagination
          v-if="total > perPage"
          v-model="currentPage"
          :per-page="perPage"
          :total-rows="total"
          aria-controls="project-users-table"
          @input="getUsers()"
        />
      </div>
    </b-overlay>
  </b-modal>
</template>

<script setup>
import useApollo from '@/plugins/graphql/useApollo'
import {
  BModal, BTable, BOverlay, BFormCheckbox, BPagination, BAvatar, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import _ from 'lodash'
import { getCurrentInstance, onMounted, ref } from 'vue'
import store from '@/store'
import i18n from '@/libs/i18n'

const props = defineProps({
  tag: {
    type: Object,
    required: true,
  },
})

const root = getCurrentInstance().proxy.$root
const emit = defineEmits(['refetch-tags'])

const users = ref([])
const keyword = ref('')
const total = ref(0)
const perPage = ref(10)
const currentPage = ref(1)
const isLoading = ref(false)
const fields = [
  { key: 'name', label: i18n.t('User') },
  {
    key: 'actions', label: i18n.t('Assign'), thStyle: 'width: 10%;', tdClass: 'text-center',
  },
]

const getUsers = _.debounce(() => {
  isLoading.value = true
  useApollo.users.searchUser({
    keyword: keyword.value,
    projectId: store.state.project.selectedProject,
    page: currentPage.value,
    first: perPage.value,
  }).then(response => {
    users.value = response.data.users.data
    total.value = response.data.users.total
  }).finally(() => {
    isLoading.value = false
  })
}, 500)

const updateTag = (user, assign) => {
  let tagIds = user.tags?.map(tag => tag.id) || []
  if (assign) {
    tagIds.push(props.tag.id)
  } else {
    tagIds = tagIds.filter(id => id !== props.tag.id)
  }

  useApollo.project.assignTagsToUser({
    projectUid: store.state.project.selectedProject,
    userUid: user.value,
    tagIds,
  }).then(response => {
    root.showSuccessMessage({
      data: { message: response.data.assignTagsToUser.message },
    })
    emit('refetch-tags')
  })
}

onMounted(() => {
  getUsers()
})
</script>

<style scoped>
#project-users-table>>>tbody tr {
  height: 50px;
}

#project-users-table>>>tbody td {
  vertical-align: middle !important;
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}
</style>
